import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { homePathForRole } from "@/lib/roleRouter";

/**
 * iter71d · Dedicated Vendor Sign-In page (marketing-grade design that
 * matches the attachment supplied by the user).
 *
 * Split-screen layout:
 *   Left  — full-bleed golf shop photography w/ the "MY CREST CLUB /
 *           VENDOR PORTAL / SEE IT. REQUEST IT. WEAR IT WITH YOUR CREST."
 *           tagline overlay.
 *   Right — cream card, "Welcome Back" headline, email + password with
 *           icons, remember-me, forgot password, Sign In, OR divider,
 *           Google sign-in, "New to My Crest Club? Request Vendor Access".
 *
 * Only vendors land on `/vendor/login`. Other roles are pushed back to the
 * shared `/login`.
 */
const HERO_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_fairway-orders/artifacts/68err38p_Vendor%20Sign%20in%20Page.png";

export default function VendorSignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role === "vendor") {
      navigate(homePathForRole("vendor"), { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      if (u.role !== "vendor") {
        setError("This sign-in is for vendor accounts. Please use the club or member sign-in.");
        return;
      }
      const from = location.state?.from?.pathname;
      navigate(from || homePathForRole("vendor"), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Sign-in failed.");
    } finally { setSubmitting(false); }
  };

  const googleSignIn = () => {
    // Sign in with Google is fronted by the Emergent-managed OAuth flow that
    // the platform already uses on the shared /login page. We forward there
    // with `?intent=vendor` so the OAuth callback lands the vendor on their
    // portal home.
    const base = window.location.origin;
    window.location.href = `${base}/auth/google?intent=vendor&next=${encodeURIComponent("/vendor")}`;
  };

  return (
    <div className="min-h-screen bg-[#0f1a14] flex flex-col" data-testid="vendor-signin-page">
      <div className="pt-6 pb-3 text-center text-[10px] uppercase tracking-[0.36em] text-[#D9C68A]">
        Vendor Sign In Page
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-8">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 shadow-2xl overflow-hidden">
          {/* Left — hero */}
          <div className="relative min-h-[480px] hidden lg:block"
               data-testid="vendor-signin-hero">
            <img
              src={HERO_IMG}
              alt="Golf shop crest apparel"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/60" />
            <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-10 text-center text-[#F9F8F4]">
              <h1 className="font-serif text-4xl sm:text-5xl tracking-[0.14em]">MY CREST CLUB</h1>
              <div className="mt-3 w-32 h-px bg-[#D9C68A]/70" />
              <div className="mt-3 text-xs uppercase tracking-[0.36em] text-[#D9C68A]">Vendor Portal</div>
              <p className="mt-8 text-[13px] uppercase tracking-[0.32em] text-[#F9F8F4]/90">
                See It. Request It.
              </p>
              <p className="mt-1 text-[13px] uppercase tracking-[0.32em] text-[#F9F8F4]/90">
                Wear It With Your Crest.
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-cream px-8 sm:px-14 py-14">
            <div>
              <h2 className="font-serif text-4xl text-[#1A422D]">Welcome Back</h2>
              <p className="mt-2 text-sm text-[#4A4A4A]">Sign in to your vendor account</p>
            </div>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="vendor-signin-form">
              <div>
                <label htmlFor="v-email" className="text-[13px] font-semibold text-[#1A1A1A] block mb-2">Email Address</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
                  <input
                    id="v-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="vendor-signin-email"
                    className="input-field w-full pl-9"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="v-password" className="text-[13px] font-semibold text-[#1A1A1A] block mb-2">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
                  <input
                    id="v-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="vendor-signin-password"
                    className="input-field w-full pl-9 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    data-testid="vendor-signin-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A7A7A] hover:text-[#1A422D]"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="inline-flex items-center gap-2 text-[#4A4A4A]">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="vendor-signin-remember"
                    className="h-4 w-4 accent-[#1A422D]"
                  />
                  Remember me
                </label>
                <Link to="/forgot-password"
                      data-testid="vendor-signin-forgot"
                      className="text-[#1A422D] hover:underline">
                  Forgot password?
                </Link>
              </div>

              {error && (
                <div className="border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm"
                     data-testid="vendor-signin-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email || !password}
                data-testid="vendor-signin-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-3.5 text-sm uppercase tracking-[0.16em] font-semibold hover:bg-[#0f1a14] disabled:opacity-50"
              >
                <Lock size={14} /> {submitting ? "Signing in…" : "Sign In"}
              </button>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[#E5E3D8]" />
                <span className="text-[10px] uppercase tracking-[0.32em] text-[#7A7A7A]">OR</span>
                <div className="flex-1 h-px bg-[#E5E3D8]" />
              </div>

              <button
                type="button"
                onClick={googleSignIn}
                data-testid="vendor-signin-google"
                className="w-full inline-flex items-center justify-center gap-3 bg-white border border-[#E5E3D8] text-[#1A1A1A] px-5 py-3 text-sm hover:border-[#1A422D]"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg"
                  alt="" aria-hidden="true"
                  className="h-5 w-5"
                />
                Sign in with Google
              </button>
            </form>

            <div className="mt-8 text-center">
              <div className="text-sm text-[#1A1A1A]">New to My Crest Club?</div>
              <Link
                to="/vendors/apply"
                data-testid="vendor-signin-request-access"
                className="mt-1 inline-block text-sm font-semibold text-[#1A422D] hover:underline"
              >
                Request Vendor Access
              </Link>
            </div>

            <div className="mt-8 pt-5 border-t border-[#E5E3D8] flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A]">
              <Link to="/login" data-testid="vendor-signin-club-login-link" className="hover:text-[#1A422D]">
                Club or Member Sign-In
              </Link>
              <span>·</span>
              <Link to="/vendors/back-office" data-testid="vendor-signin-passkey-link" className="hover:text-[#1A422D]">
                Team Passkey
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
